import {
  Component,
  ViewEncapsulation,
  ViewChild
} from '@angular/core';
import {
  AbstractBlStaticGuide,
  ModalReason,
  BlDialogService,
  BlLoadingService,
  ModalButtonSetting,
  ModalButtonColor,
  BlModalContentComponent,
  ModalOption,
  ModalButtonType,
  BlSortableComponent
} from '@blcloud/bl-ng-ui-component';
import { ExportPatternModalService } from './export-pattern-modal.service';
import {
  forEach as _forEach,
  isNil as _isNil,
  defer as _defer,
  delay as _delay,
  assign as _assign
} from 'lodash';
import {
  IExportPatternInfo
} from '@blcloud/bl-datamodel';
import {
  ExportPatternCodeConstant,
  IExportPatternModalResult
} from './export-pattern-modal.define';

@Component({
  templateUrl: './export-pattern-modal.component.html',
  styleUrls: ['./export-pattern-modal.component.scss'],
  providers: [ExportPatternModalService],
  encapsulation: ViewEncapsulation.None,
})
export class ExportPatternModalComponent extends AbstractBlStaticGuide {
  @ViewChild('content') blModalContentComponent: BlModalContentComponent;

  @ViewChild('blSortable') blSortable: BlSortableComponent;

  /** ガイドフッターのボタン配置 */
  footerButtonType = ModalButtonType.OkCancel;
  okButtonSetting: ModalButtonSetting = {
    color: ModalButtonColor.Success,
    label: '決定',
  };

  /** 出力パターン情報 出力データ */
  outputExportPatternInfo: IExportPatternInfo;

  /** 画面ローディング制御フラグ */
  isLoaded = false;

  /** 出力パターンモーダル結果 */
  result: IExportPatternModalResult = {};

  /** 請求売掛 */
  creditExportPatternItems = [
    { code: ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_BILLING, name: '請求' },
    { code: ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_ACCREC_BALANCE, name: '売掛(与信残高あり)' },
    { code: ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_ACCREC_NOBALANCE, name: '売掛(与信残高なし)' }
  ];

  /** 支払買掛 */
  debtExportPatternItems = [
    { code: ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_PAY, name: '支払' },
    { code: ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_ACCPAY, name: '買掛' }
  ];

  selectExportPatternItems = this.creditExportPatternItems;

  selectExportPatternCode = ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_BILLING;

  constructor(
    private service: ExportPatternModalService,
    private dialogService: BlDialogService,
    private loadingService: BlLoadingService
  ) {
    super();
  }

  /**
   * @override
   */
  getResults() {
    return this.result;
  }

  /**
   * @override
   */
  defaultOption(): ModalOption {
    return { class: 'modelsize-middle', fullHeight: true };
  }

  /**
   * 画面表示後にデータローディング処理
   */
  onShown() {
    this.loadingService.show({ message: 'ローディング中' });
    this.selectExportPatternCode = this.data.exportPatternCode;
    const creditCodes = [
      ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_BILLING,
      ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_ACCREC_BALANCE,
      ExportPatternCodeConstant.PATTERN_CODE_ALL_ITEM_ACCREC_NOBALANCE
    ];
    if (creditCodes.includes(this.data.exportPatternCode)) {
      this.selectExportPatternItems = this.creditExportPatternItems;
    } else {
      this.selectExportPatternItems = this.debtExportPatternItems;
    }
    const exportPatternInfoSessionData =
      this.service.getExportPatternInfoFromSession(
        this.data.dataExportPatterns
      );

    // セッションにデータがあった場合、セッションからデータを表示する。
    if (!_isNil(exportPatternInfoSessionData)) {
      this.initializationDataModal(exportPatternInfoSessionData);
      this.loadingService.hide();
      // セッションにデータがない場合、APIよりデータベースから取得し、画面に表示する。
    } else {
      this.refreshPatternInfo();
    }
  }

  /**
   * 初期表示処理
   */
  initializationDataModal(data): void {
    let tempOutputExportPatternInfo: IExportPatternInfo;
    _forEach(data, (items) => {
      // 出力情報設定（テキスト出力パターンコードで判定）
      if (items.exportPatternCode.toString() === this.selectExportPatternCode) {
        tempOutputExportPatternInfo = _assign({}, items);
      }
    });
    // セッションデータに該当データ存在しない場合、モーダルを閉じる
    if (_isNil(tempOutputExportPatternInfo)) {
      this.dialogService
        .warn('対象の出力パターン情報を取得できませんでした', 'エラー')
        .subscribe((modalRef) => {
          const el = document.activeElement;
          modalRef.hide(el);
          this.blModalContentComponent.cancel();
        });
    }
    _defer(() => {
      this.outputExportPatternInfo = tempOutputExportPatternInfo;
      // フォーカス移動
      _delay(() => {
        if (this.blSortable) {
          this.blSortable.focus();
        }
      }, 10);
    });
    this.isLoaded = true;
  }

  /**
   * ダイアログフッタのボタン上での処理
   * @param $event Event
   */
  btnKeyDownEvent($event): void {
    switch ($event) {
      case ModalReason.Done:
        this.onClickUpdate();
        break;
      default:
        break;
    }
  }

  /**
   * 出力パターン情報更新処理
   */
  onClickUpdate() {
    if (this.outputExportPatternInfo) {
      this.service
        .updateExportPatternInfo(
          this.selectExportPatternCode,
          this.outputExportPatternInfo
        )
        .subscribe(
          (res) => {
            if (res) {
              // ローディング処理終了
              this.loadingService.hide();
              // モーダルを閉じる
              this.blModalContentComponent.cancel();
            }
          },
          (error) => {
            // ローディング処理終了
            this.loadingService.hide();
            // エラー通知
            this.dialogService
              .error(error.messages, 'エラー')
              .subscribe((modalRef) => {
                const el = document.activeElement;
                modalRef.hide(el);
              });
          }
        );
    }
  }

  /**
   * テキスト出力ヘッダ情報配列変更イベントハンドラ
   * @param event
   */
  onChangeExportItemHeaderInfoList(event) {
    if (!_isNil(event) && event.length !== 0) {
      this.outputExportPatternInfo.exportItemHeaderInfoList = event;
    }
  }

  /** チェックボックス選択イベント */
  onChangePatternName(event) {
    this.selectExportPatternCode = event;
    this.refreshPatternInfo();
  }

  /** 出力パターン情報を取得し、画面を更新する */
  refreshPatternInfo() {
    this.service
      .getExportPatternInfo(this.selectExportPatternCode.toString())
      .subscribe((res) => {
        if (res) {
          this.initializationDataModal(res);
        } else {
          this.dialogService
            .warn('対象の出力パターン情報を取得できませんでした', 'エラー')
            .subscribe((modalRef) => {
              const el = document.activeElement;
              modalRef.hide(el);
              this.blModalContentComponent.cancel();
            });
        }
        this.loadingService.hide();
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/export-modal/export-pattern-modal/export-pattern-modal.component.ts